import { Injectable, inject } from '@angular/core';
import { TimeconversionService } from '@fitness-force/api';
import type { InvoiceOccurance } from '@fitness-force/models';

export interface FreezeDateRange {
  start_date?: string;
  end_date?: string;
  approved_start_date?: string;
  approved_end_date?: string;
  requested_start_date?: string;
  requested_end_date?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FreezeDateService {
  private timeService = inject(TimeconversionService);

  /**
   * Prepare date ranges for the API call.
   * Handles both billing cycle multiselect and date-picker modes.
   */
  prepareDatesForAPI(options: {
    isRecurring: boolean;
    selectedAttempts: InvoiceOccurance[] | null;
    startDateValue: Date | null;
    endDateValue: Date | null;
  }): FreezeDateRange[] {
    if (options.isRecurring && options.selectedAttempts?.length) {
      const sorted = this.sortCycles(options.selectedAttempts);
      return [{
        start_date: this.toUtcIsoString(sorted[0].occurance_start_date),
        end_date: this.toUtcIsoString(sorted[sorted.length - 1].occurance_end_date),
      }];
    }

    if (options.startDateValue && options.endDateValue) {
      const endDateCopy = new Date(options.endDateValue);
      endDateCopy.setHours(23, 59, 59);
      return [{
        start_date: this.timeService.ConvertLocalDateTimeToUTC(options.startDateValue),
        end_date: this.timeService.ConvertLocalDateTimeToUTC(endDateCopy),
      }];
    }

    return [];
  }

  /**
   * Prepare request_dates for freeze creation (no payment).
   */
  prepareRequestDates(options: {
    isRecurring: boolean;
    freezeCanOnlyBillingCycle: boolean;
    selectedAttempts: InvoiceOccurance[] | null;
    startDateValue: Date | null;
    endDateValue: Date | null;
  }): Array<{ requested_start_date: string; requested_end_date: string }> {
    if (options.isRecurring && options.freezeCanOnlyBillingCycle && options.selectedAttempts?.length) {
      const sorted = this.sortCycles(options.selectedAttempts);
      return [{
        requested_start_date: this.convertDate(sorted[0].occurance_start_date),
        requested_end_date: this.convertDate(sorted[sorted.length - 1].occurance_end_date),
      }];
    }

    if (options.startDateValue && options.endDateValue) {
      const endDate = new Date(options.endDateValue);
      endDate.setHours(23, 59, 59);
      return [{
        requested_start_date: this.timeService.ConvertLocalDateTimeToUTC(options.startDateValue),
        requested_end_date: this.timeService.ConvertLocalDateTimeToUTC(endDate),
      }];
    }

    return [];
  }

  /**
   * Prepare dates for freeze creation with payment.
   * Returns an object with both approved_ and requested_ date fields.
   */
  preparePaymentDates(options: {
    isRecurring: boolean;
    freezeCanOnlyBillingCycle: boolean;
    selectedAttempts: InvoiceOccurance[] | null;
    startDateValue: Date | null;
    endDateValue: Date | null;
  }): FreezeDateRange[] {
    if (options.isRecurring && options.freezeCanOnlyBillingCycle && options.selectedAttempts?.length) {
      const sorted = this.sortCycles(options.selectedAttempts);
      const startDate = this.toUtcIsoString(sorted[0].occurance_start_date);
      const endDate = this.toUtcIsoString(sorted[sorted.length - 1].occurance_end_date);
      return [{
        approved_start_date: startDate,
        approved_end_date: endDate,
        requested_start_date: startDate,
        requested_end_date: endDate,
      }];
    }

    if (options.startDateValue && options.endDateValue) {
      const endDate = new Date(options.endDateValue);
      endDate.setHours(23, 59, 59);
      const startUtc = this.timeService.ConvertLocalDateTimeToUTC(options.startDateValue);
      const endUtc = this.timeService.ConvertLocalDateTimeToUTC(endDate);
      return [{
        approved_start_date: startUtc,
        approved_end_date: endUtc,
        requested_start_date: startUtc,
        requested_end_date: endUtc,
      }];
    }

    return [];
  }

  /**
   * Prepare approved and request dates for approve mode.
   */
  prepareApproveDates(options: {
    isRecurring: boolean;
    freezeCanOnlyBillingCycle: boolean;
    selectedAttempts: InvoiceOccurance[] | null;
    startDateValue: Date | null;
    endDateValue: Date | null;
    fallbackStartDate?: string;
    fallbackEndDate?: string;
  }): { approved_dates: FreezeDateRange[]; request_dates: FreezeDateRange[] } {
    const approved_dates: FreezeDateRange[] = [];
    const request_dates: FreezeDateRange[] = [];

    if (options.isRecurring && options.freezeCanOnlyBillingCycle && options.selectedAttempts?.length) {
      const sorted = this.sortCycles(options.selectedAttempts);
      approved_dates.push({
        approved_start_date: sorted[0].occurance_start_date,
        approved_end_date: sorted[sorted.length - 1].occurance_end_date,
      });
      request_dates.push({
        requested_start_date: this.convertDate(sorted[0].occurance_start_date),
        requested_end_date: this.convertDate(sorted[sorted.length - 1].occurance_end_date),
      });
    } else if (options.startDateValue && options.endDateValue) {
      const endDateCopy = new Date(options.endDateValue);
      endDateCopy.setHours(23, 59, 59);
      const startUtc = this.timeService.ConvertLocalDateTimeToUTC(options.startDateValue);
      const endUtc = this.timeService.ConvertLocalDateTimeToUTC(endDateCopy);

      approved_dates.push({ approved_start_date: startUtc, approved_end_date: endUtc });
      request_dates.push({ requested_start_date: startUtc, requested_end_date: endUtc });
    } else if (options.fallbackStartDate && options.fallbackEndDate) {
      approved_dates.push({
        approved_start_date: options.fallbackStartDate,
        approved_end_date: options.fallbackEndDate,
      });
      request_dates.push({
        requested_start_date: options.fallbackStartDate,
        requested_end_date: options.fallbackEndDate,
      });
    }

    return { approved_dates, request_dates };
  }

  /**
   * Prepare start/end UTC strings for the cycle adjustment API.
   * Handles both billing-cycle (UTC as-is) and date-picker (needs conversion) modes.
   */
  prepareCycleAdjustmentDates(options: {
    selectedCycles?: InvoiceOccurance[];
    startDateValue?: Date | null;
    endDateValue?: Date | null;
  }): { requestedStartDate: string; requestedEndDate: string } | null {
    if (options.selectedCycles?.length) {
      const sorted = [...options.selectedCycles].sort(
        (a, b) =>
          new Date(a.occurance_start_date).getTime() -
          new Date(b.occurance_start_date).getTime()
      );
      return {
        requestedStartDate: this.toUtcIsoString(sorted[0].occurance_start_date),
        requestedEndDate: this.toUtcIsoString(sorted[sorted.length - 1].occurance_end_date),
      };
    }

    if (!options.startDateValue || !options.endDateValue) return null;

    const endDateCopy = new Date(options.endDateValue);
    endDateCopy.setHours(23, 59, 59);

    return {
      requestedStartDate: this.timeService.ConvertLocalDateTimeToUTC(options.startDateValue),
      requestedEndDate: this.timeService.ConvertLocalDateTimeToUTC(endDateCopy),
    };
  }

  /**
   * Calculate freeze days between two dates
   */
  calculateFreezeDays(startDate: Date | null, endDate: Date | null): number {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0);
    end.setHours(23, 59, 59);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
  }

  /** Returns cycles sorted ascending by start date. */
  sortBillingCycles(cycles: InvoiceOccurance[]): InvoiceOccurance[] {
    return this.sortCycles(cycles);
  }

  /**
   * Corrects freeze request end dates that were stored as a cycle start date
   * instead of the last selected cycle's end date.
   */
  resolveRequestedFreezeEndDate(
    requestedStartDate: string | null | undefined,
    requestedEndDate: string | null | undefined,
    billingCycles: InvoiceOccurance[]
  ): string | null {
    if (!requestedEndDate) {
      return null;
    }

    if (!billingCycles?.length) {
      return requestedEndDate;
    }

    const sorted = this.sortCycles(billingCycles);

    if (
      requestedStartDate &&
      this.areSameOccurrenceStartDate(requestedStartDate, requestedEndDate)
    ) {
      const startCycle = sorted.find((cycle) =>
        this.areSameOccurrenceStartDate(
          cycle.occurance_start_date,
          requestedStartDate
        )
      );
      if (startCycle?.occurance_end_date) {
        return startCycle.occurance_end_date;
      }
    }

    const endMatchesCycleStart = sorted.find((cycle) =>
      this.areSameOccurrenceStartDate(
        cycle.occurance_start_date,
        requestedEndDate
      )
    );
    if (endMatchesCycleStart?.occurance_end_date) {
      return endMatchesCycleStart.occurance_end_date;
    }

    return requestedEndDate;
  }

  private sortCycles(cycles: InvoiceOccurance[]): InvoiceOccurance[] {
    return [...cycles].sort(
      (a, b) =>
        new Date(a.occurance_start_date).getTime() -
        new Date(b.occurance_start_date).getTime()
    );
  }

  private areSameOccurrenceStartDate(
    firstDate: string | Date,
    secondDate: string | Date
  ): boolean {
    return (
      new Date(firstDate).getTime() === new Date(secondDate).getTime()
    );
  }

  /**
   * Validates that the selected cycles are sequential (no gaps).
   * Returns true when valid, false when a gap exists.
   */
  areSelectedCyclesSequential(
    allCycles: InvoiceOccurance[],
    selectedCycles: InvoiceOccurance[]
  ): boolean {
    if (!selectedCycles?.length) return true;

    const sortedAll = this.sortCycles(allCycles);
    const sortedSelected = this.sortCycles(selectedCycles);

    const firstSelectedIndex = sortedAll.findIndex((cycle) =>
      this.areSameOccurrenceStartDate(
        cycle.occurance_start_date,
        sortedSelected[0].occurance_start_date
      )
    );

    if (firstSelectedIndex === -1) return false;

    for (let i = 0; i < sortedSelected.length; i++) {
      const expectedCycle = sortedAll[firstSelectedIndex + i];
      if (
        !expectedCycle ||
        !this.areSameOccurrenceStartDate(
          expectedCycle.occurance_start_date,
          sortedSelected[i].occurance_start_date
        )
      ) {
        return false;
      }
    }

    return true;
  }

  private convertDate(date: string): string {
    const localDate = this.timeService.ConvertUTCToLocalDateTime(date);
    return this.timeService.ConvertLocalDateTimeToUTC(localDate);
  }

  /** Appends UTC suffix only when the value is not already ISO-UTC formatted. */
  private toUtcIsoString(date: string): string {
    return date.endsWith('Z') ? date : `${date}Z`;
  }
}
